import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import Icon from "shared/components/Icon";
import CreateNewDropdown from "features/analytics/studio/components/boards/CreateNewDropdown";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primaryHover } from "shared/theme/trodoColors";
import { useBreadcrumbSegments, useHideGlobalTopBar } from "shared/context/BreadcrumbContext";

export default function AnalyticsTopBar({
  breadcrumbs = [],
  showCreateNew = true,
  showTimeRange = true,
  onSave,
  onClose,
  showSaveClose = false,
  saveOnly = false,
  saveStatus,
  reorderSaving = false,
  saveDisabled = false,
  showBoardActions = false,
  onShare,
  onDelete,
  onRename,
  /** When true, board is view-only: hide save, hide destructive/edit board actions, Share may still open (read-only areas inside modal). */
  boardReadOnly = false,
  /** Ref attached to the primary Save button (for positioning save-to-board popover). */
  saveAnchorRef = null,
  /** Inline chart/report name (single editable field after breadcrumbs; placeholder "Name"). */
  chartName,
  onChartNameChange,
  /** Slot rendered on the far left — board icon + title + description etc. */
  leftSlot,
  /** Slot rendered right before Share button — e.g. BoardTimeButton. */
  timeSlot,
}) {
  const { team } = useParams();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const createAnchorRef = useRef(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);
  const segments = useBreadcrumbSegments();

  // Editor mode: chartName input + save/close all collapse into one unified h-8 bar
  // that replaces the standalone GlobalTopBar breadcrumb row.
  const isEditorMode =
    showSaveClose && !saveOnly && typeof chartName === "string" && typeof onChartNameChange === "function";

  // Hide the standalone GlobalTopBar while the editor's unified bar is shown.
  useHideGlobalTopBar(isEditorMode);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreMenuOpen]);

  // ── Editor mode: one unified bar ─────────────────────────────────────────
  // Renders: [breadcrumb segments] / [editable chart name]   [Close] [Save]
  if (isEditorMode) {
    return (
      <div
        className="flex h-8 shrink-0 items-center gap-0 border-b border-[color:var(--border-default)] bg-surface px-4"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {/* Breadcrumb segments: all are parent links */}
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 shrink-0">
          {segments.map((seg, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <span className="shrink-0 text-[13px] text-[color:var(--text-muted)]" aria-hidden>/</span>
              )}
              <Link
                to={seg.to || "#"}
                className="shrink-0 text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
              >
                {seg.label}
              </Link>
            </React.Fragment>
          ))}
        </nav>

        {/* Separator + editable chart name */}
        {segments.length > 0 && (
          <span className="mx-1.5 shrink-0 text-[13px] text-[color:var(--text-muted)]" aria-hidden>/</span>
        )}
        <input
          type="text"
          value={chartName}
          onChange={onChartNameChange}
          placeholder="Untitled"
          aria-label="Chart name"
          className="min-w-[6rem] max-w-[min(24rem,38vw)] flex-1 bg-transparent text-[13px] font-medium text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
          style={{ fontFamily: "Inter, sans-serif" }}
        />

        {/* Right: status + Close + Save */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {(saveStatus || reorderSaving) && (
            <span className="text-[11px] font-medium text-[color:var(--text-muted)]">
              {saveStatus === "saving" || reorderSaving ? "Saving…" : saveStatus === "saved" ? "Saved" : ""}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-6 items-center rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
          >
            Close
          </button>
          <button
            ref={saveAnchorRef}
            type="button"
            onClick={onSave}
            disabled={saveStatus === "saving" || reorderSaving || saveDisabled}
            className="inline-flex h-6 items-center rounded-md px-2.5 text-[12px] font-semibold text-ink-onBrand transition-colors disabled:opacity-60"
            style={{ backgroundColor: "var(--brand-primary)" }}
            onMouseEnter={(e) => { if (!saveDisabled) e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary)"; }}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  // ── Normal mode ───────────────────────────────────────────────────────────
  // The GlobalTopBar in AnalyticsLayout already renders the breadcrumb path
  // (Reports / Board name). This bar only renders the action strip below it.
  // Nothing is rendered when there are no actions to show.
  const hasActions = showBoardActions || showSaveClose || showCreateNew
    || typeof chartName === "string" || !!saveStatus || !!leftSlot || !!timeSlot;

  if (!hasActions) return null;

  return (
    <div
      className="flex items-center gap-2 border-b border-[color:var(--border-default)] bg-surface px-4 py-1.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Left slot: board icon + title/description or other page-level identity */}
      {leftSlot && (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {leftSlot}
        </div>
      )}

      {/* Editable chart name (non-editor pages that still need an inline name field) */}
      {!isEditorMode && typeof chartName === "string" && typeof onChartNameChange === "function" && (
        <input
          type="text"
          value={chartName}
          onChange={onChartNameChange}
          className="min-w-[8rem] max-w-[min(22rem,42vw)] border-b bg-transparent py-0.5 text-[13px] font-medium outline-none transition-colors"
          style={{ color: "var(--text-primary)", borderColor: "transparent", fontFamily: "Inter, sans-serif" }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "var(--brand-primary)"; }}
          onBlur={(e)  => { e.currentTarget.style.borderColor = "transparent"; }}
          placeholder="Name"
          aria-label="Chart name"
        />
      )}

      {/* Save-status indicator (left of actions) */}
      {saveStatus && !saveOnly && (
        <span className="text-[11px] font-medium text-[color:var(--text-muted)]">
          {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : ""}
        </span>
      )}

      {/* Create New */}
      {showCreateNew && (
        <div className="relative" ref={createAnchorRef}>
          <button
            type="button"
            onClick={() => setCreateOpen((o) => !o)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[12px] font-semibold text-ink-onBrand transition-colors"
            style={{ backgroundColor: "var(--brand-primary)" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary)"; }}
          >
            <span className="text-sm leading-none">+</span>
            Create new
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <CreateNewDropdown open={createOpen} onClose={() => setCreateOpen(false)} anchorRef={createAnchorRef} />
        </div>
      )}

      {/* Push board actions + save to the right */}
      <div className={`${leftSlot ? "" : "ml-auto"} flex items-center gap-2`}>
        {/* Time range slot (e.g. board time filter button) */}
        {timeSlot}

        {/* Share + ⋯ menu */}
        {showBoardActions && (
          <>
            <button
              type="button"
              onClick={() => onShare?.()}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
            >
              <Icon name="share" size={14} color="var(--text-muted)" />
              Share
            </button>

            {!boardReadOnly && (
              <div className="relative" ref={moreMenuRef}>
                <button
                  type="button"
                  onClick={() => setMoreMenuOpen((o) => !o)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  aria-label="More options"
                  aria-haspopup="true"
                  aria-expanded={moreMenuOpen}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {moreMenuOpen && (
                  <div
                    className="absolute right-0 top-full z-50 mt-1 min-w-[160px] overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg"
                    role="menu"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
                      onClick={() => { setMoreMenuOpen(false); onRename?.(); }}
                    >
                      <Icon name="edit_pencil" size={14} color="var(--text-muted)" />
                      Rename
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[color:var(--feedback-error)] transition-colors hover:bg-[color:var(--feedback-error-bg)]"
                      onClick={() => { setMoreMenuOpen(false); onDelete?.(); }}
                    >
                      <Icon name="delete" size={14} color="var(--feedback-error)" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Save / Close */}
        {showSaveClose && (saveOnly ? (
          boardReadOnly ? (
            <span className="text-[11px] font-medium text-[color:var(--text-muted)]">View only</span>
          ) : (
            <>
              {(saveStatus || reorderSaving) && (
                <span className="text-[11px] font-medium text-[color:var(--text-muted)]">
                  {saveStatus === "saving" || reorderSaving ? "Saving…" : saveStatus === "saved" ? "Saved" : ""}
                </span>
              )}
              <button
                ref={saveAnchorRef}
                type="button"
                onClick={onSave}
                disabled={saveStatus === "saving" || reorderSaving || saveDisabled}
                className="inline-flex h-8 items-center rounded-md px-3 text-[12px] font-semibold text-ink-onBrand transition-colors disabled:opacity-60"
                style={{ backgroundColor: "var(--brand-primary)" }}
                onMouseEnter={(e) => { if (!saveDisabled) e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary)"; }}
              >
                Save
              </button>
            </>
          )
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 items-center rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
            >
              Close
            </button>
            <button
              ref={saveAnchorRef}
              type="button"
              onClick={onSave}
              className="inline-flex h-8 items-center rounded-md px-3 text-[12px] font-semibold text-ink-onBrand transition-colors"
              style={{ backgroundColor: "var(--brand-primary)" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary)"; }}
            >
              Save
            </button>
          </>
        ))}
      </div>
    </div>
  );
}
